import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IFarm } from 'interfaces/IFarm';
import type { IZone } from 'interfaces/IZone';
import FarmService from 'services/FarmService';

import { isFarm, isZone, writeFarmToCache, writeZonesToCache } from './farmsCache';

interface UpsertFarmArgs {
  farm: IFarm;
  /** Changed paddock zones, saved in the same call. */
  zones?: IZone[];
}

/**
 * Creates, updates, excludes or re-includes a farm through Farm/Upsert
 * (OP-2481). Build the farm with the utils/farmPayloads builders — excluding
 * cascades to the farm's paddocks in the same payload. Paddock edits ride
 * along with their zones, so a farm's changes are one save.
 */
export const useUpsertFarm = (orgId: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ farm, zones }: UpsertFarmArgs) =>
      FarmService.Upsert(farm, zones),
    onSuccess: (response, { farm, zones }) => {
      if (!orgId) return;

      const data = response?.data ?? [];
      const savedFarm = data.find(isFarm);
      const savedZones = data.filter(isZone);

      writeFarmToCache(queryClient, orgId, savedFarm ?? farm);

      if (savedZones.length > 0) {
        writeZonesToCache(queryClient, orgId, savedZones);
      } else if (zones?.length) {
        // No zones in the response — patch the zones we sent into the cache.
        writeZonesToCache(queryClient, orgId, zones);
      }
    },
  });
};

export default useUpsertFarm;
